import { Tooltip } from '@tableau/widgets';
import React from 'react';

import { Formatters, Messages } from '~src/codegen/Localize';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { cssClass } from '~src/style';

import { FlowParameter } from '../types/FlowParameters';
import { getFlowTimestampTypeName } from '../utils/FlowParametersUtils';
import { FlowParameterInput } from './FlowParameterInput';

const rootClass = 'FlowParametersDialogContent';

const wrapper = cssClass(rootClass, 'wrapper', {
  padding: '18px 0px 8px 0px',
  maxHeight: 300,
  overflow: 'auto',
});

const separator = cssClass(rootClass, 'separator', {
  width: '100%',
  borderTop: '1px solid #ccc',
  margin: '16px 0px',
});

const parametersHeader = cssClass(rootClass, 'parametersHeader', {
  fontWeight: 500,
});

const parameterWrapper = cssClass(rootClass, 'parameterWrapper', {
  $nest: {
    '&:not(:last-of-type)': {
      marginBottom: 20,
    }
  }
});

export const parameterSection = cssClass(rootClass, 'parameterSection', {
  margin: '0px 16px',
});

const parametersList = cssClass(rootClass, 'parametersList', {
  margin: '12px 8px',
});

const parameterLabel = cssClass(rootClass, 'parameterLabel', {
  display: 'flex',
  alignItems: 'center',
});

const systemParameterLabel = cssClass(rootClass, 'systemParameterLabel', {
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
});

export const systemParametersMessage = cssClass(rootClass, 'systemParametersMessage', {
  margin: '4px 24px',
});

const iconClass = cssClass(rootClass, 'iconClass', {
  height: 16,
  marginLeft: 6,
  verticalAlign: 'text-top',
  outlineStyle: 'none',
});

const infoIcon = 'info_normal.svg';

export type FlowParametersSetParametersAction =
  { type: 'FlowParametersSetParameters', payload: {[key: string]: string} };

  // Actions can be taken by both Linked Tasks and Flow Tasks
export type FlowParametersActions = FlowParametersSetParametersAction;

export type FlowParametersDialogContentProps = {
  requiredParameters: FlowParameter[];
  additionalParameters: FlowParameter[];
  systemParameters: FlowParameter[];
  isRunNow: boolean;
  actionHandler: (action: FlowParametersActions) => void;
  overrideValues?: {[key: string]: string};
}

export function FlowParametersDialogContent(props: FlowParametersDialogContentProps): JSX.Element {
  const {requiredParameters, additionalParameters, systemParameters, isRunNow, actionHandler, overrideValues} = props;
  const [parametersMap, setParametersMap] = React.useState<{[key: string]: string}>({});
  const parametersRef = React.useRef(parametersMap);

  React.useEffect(() => {
    parametersRef.current = parametersMap;
  }, [parametersMap]);

  React.useEffect(() => {
    if (Object.keys(parametersMap).length === 0) {
      if (overrideValues !== undefined && Object.keys(overrideValues).length > 0) {
        setParametersMap(overrideValues);
      } else {
        const defaultValues: {[key: string]: string} = {};
        requiredParameters.map((parameter) => {
          defaultValues[parameter.id] = '';
        });
        additionalParameters.map((parameter) => {
          if (parameter.value) {
            defaultValues[parameter.id] = parameter.value;
          }
        });
        setParametersMap(defaultValues);
        if (isRunNow) {
          actionHandler({ type: 'FlowParametersSetParameters', payload: defaultValues});
        }
      }
    }
  }, [requiredParameters, additionalParameters, overrideValues, isRunNow, actionHandler, parametersMap]);

  React.useEffect(() => {
    // On cleanup we send the action to update the overarching state
    return () => {
      !isRunNow && actionHandler({ type: 'FlowParametersSetParameters', payload: parametersRef.current});
    };
  }, [actionHandler, isRunNow]);

  function updateParameter(parameterId: string, value: string) {
    const newMap = {
      ...parametersMap,
      [parameterId]: value
    };
    setParametersMap(newMap);
    if (isRunNow) {
      actionHandler({ type: 'FlowParametersSetParameters', payload: newMap});
    }
  }

  function renderDescription(description: string): JSX.Element {
    return (
      <Tooltip getContent={() => <div>{description}</div>}>
        <img className={iconClass} src={imgPath(infoIcon)} alt={Messages.flowParameterDescription()}/>
      </Tooltip>
    );
  }

  function renderParameter(parameterProps: FlowParameter): JSX.Element {
    const props = {
      parameter: parameterProps,
      value: parametersMap[parameterProps.id],
      updateValue: updateParameter
    };
    return parameterProps.domain.domainType !== 'system' ? (
      <div key={parameterProps.id} className={parameterWrapper}>
        <div className={parameterLabel}>
          <div>{parameterProps.name}</div>
          {parameterProps.description ? renderDescription(parameterProps.description) : null}
        </div>
        <FlowParameterInput {...props}/>
      </div>
    ) : (
      <div key={parameterProps.id} className={parameterWrapper}>
        <div className={systemParameterLabel}>
          {`<${getFlowTimestampTypeName(parameterProps.value)}>`}
        </div>
      </div>
    );
  }

  function renderParameterSection(header: string, parameters: FlowParameter[]): JSX.Element {
    return (
      <div className={parameterSection}>
        <div className={parametersHeader}>{header}</div>
        <div className={parametersList}>
          { parameters.map(parameter => renderParameter(parameter)) }
        </div>
      </div>
    );
  }

  const userParameterCount = requiredParameters.length + additionalParameters.length;
  const systemParameterCount = systemParameters.length;

  // V2 Parameters, showing user parameters and system parameters
  function parametersV2() {
    return (
      <>
        {userParameterCount > 0 ? renderParameterSection(Messages.flowParametersUserParameters(), [...requiredParameters, ...additionalParameters]) : null}
        {userParameterCount && systemParameterCount ? <div className={separator}/> : null}
        {systemParameterCount > 0 ?
          <div>
            {renderParameterSection(Messages.flowParametersSystemParametersCount({SYSTEM_PARAMETER_COUNT: systemParameterCount, SYSTEM_PARAMETER_COUNT_LOC: Formatters.formatCount(systemParameterCount)}), systemParameters)}
            <div className={systemParametersMessage}>{Messages.flowParametersAutomaticallySet()}</div>
          </div>
          : null
        }
      </>
    );
  }
  // V1 Parameters, showing required and additional parameters
  function parametersV1() {
    return (
      <>
        {requiredParameters.length > 0 ? renderParameterSection(Messages.flowParametersRequiredParameters(), requiredParameters) : null}
        {requiredParameters.length && additionalParameters.length ? <div className={separator}/> : null}
        {additionalParameters.length > 0 ? renderParameterSection(Messages.flowParametersAdditionalParameters(), additionalParameters) : null}
      </>
    );
  }

  /* eslint-disable jsx-a11y/anchor-has-content */
  /* eslint-disable jsx-a11y/anchor-is-valid */
  return (
    <div className={wrapper}>
      <a href="" />
      {FeatureFlags.timestampOutput ? parametersV2() : parametersV1()}
    </div>
  );
}
